import { BUILT_IN_ELEMENT_TYPES, CUSTOM_TYPE_PATTERN, FORBIDDEN_KEYS, FormDefinitionSchema, } from './form.js';
import { compareVersions } from './version.js';
import { parseExpression } from '../expr/parser.js';
import { isRasdError } from '../errors.js';
import { flattenDefinition, isWideningType } from '../migrate/flatten.js';
export const DEFAULT_VALIDATION_LIMITS = Object.freeze({
    definitionBytes: 2 * 1024 * 1024,
    elements: 2000,
    pages: 1000,
    containerDepth: 6,
    repeatNesting: 3,
    inlineChoicesPerList: 10000,
    inlineDatasetRows: 10000,
    validatorsPerElement: 20,
    locales: 20,
    exprChars: 4000,
});
/**
 * Reserved storage keys (docs/04 §13). `meta` and the XLSForm/Kobo export columns
 * would collide in `submission.data`, CSV exports or SQL views.
 */
export const RESERVED_NAMES = [
    'meta',
    'id',
    'formId',
    'formVersion',
    'status',
    'data',
    'attachments',
    'audit',
    'ext',
    'instanceID',
    'deprecatedID',
    'instanceName',
    'formhub',
    'start',
    'end',
    'today',
    'deviceid',
    'username',
    'email',
    'phonenumber',
    'subscriberid',
    'simserial',
    '_id',
    '_uuid',
    '__version__',
    '_submission_time',
    '_index',
    '_parent_index',
    '_parent_table_name',
    'KEY',
    'PARENT_KEY',
];
/** REL v1 core functions (docs/05 §7) plus the ODK aliases accepted in call position (§16). */
export const CORE_FUNCTIONS = [
    // logic / emptiness
    'if',
    'coalesce',
    'empty',
    'notEmpty',
    'not',
    'boolean',
    'true',
    'false',
    // selection
    'selected',
    'selectedAt',
    'countSelected',
    'choiceLabel',
    // repeats / aggregates
    'count',
    'sum',
    'min',
    'max',
    'avg',
    'countIf',
    'position',
    'indexedRepeat',
    'join',
    // strings
    'regex',
    'contains',
    'startsWith',
    'endsWith',
    'stringLength',
    'substr',
    'concat',
    'upper',
    'lower',
    'trim',
    'string',
    // numbers
    'number',
    'int',
    'round',
    'abs',
    'pow',
    // dates
    'today',
    'now',
    'date',
    'dateDiff',
    'formatDate',
    'age',
    // identity
    'uuid',
    'random',
    'once',
    // data / geo
    'pulldata',
    'jsonPath',
    'distance',
    'area',
    // ODK hyphenated aliases (docs/05 §16)
    'selected-at',
    'count-selected',
    'string-length',
    'format-date',
    'indexed-repeat',
    'starts-with',
    'ends-with',
    'boolean-from-string',
    'decimal-date-time',
];
/** Preload metadata addressable as `${meta.*}` (docs/00 §4.4). */
export const META_KEYS = [
    'deviceId',
    'userId',
    'username',
    'startedAt',
    'now',
    'locale',
    'formVersion',
    'appVersion',
    'platform',
    'submissionId',
];
/** Element types that store no value (docs/04 §10.18). */
const VALUE_LESS_TYPES = new Set(['note', 'group']);
const CONTAINER_TYPES = new Set(['group', 'repeat']);
const SELECT_TYPES = new Set(['select_one', 'select_multiple', 'rank']);
// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
export function validateFormDefinition(def, opts = {}) {
    const errors = [];
    const warnings = [];
    const limits = resolveLimits(opts.limits);
    if (def === null || typeof def !== 'object' || Array.isArray(def)) {
        errors.push({ code: 'E_SCHEMA', path: '', message: 'Definition must be a JSON object' });
        return { ok: false, errors, warnings };
    }
    // Prototype-pollution keys are checked before anything parses the document, and
    // abort immediately: nothing downstream should touch such an object.
    findForbiddenKeys(def, '', errors);
    if (errors.length > 0)
        return { ok: false, errors, warnings };
    const bytes = utf8ByteLength(safeStringify(def));
    if (bytes > limits.definitionBytes) {
        errors.push({
            code: 'E_DEFINITION_TOO_LARGE',
            path: '',
            message: `Definition is ${bytes} B; limit ${limits.definitionBytes} B`,
        });
        return { ok: false, errors, warnings };
    }
    if (bytes > 512 * 1024) {
        warnings.push({
            code: 'W_DEFINITION_LARGE',
            path: '',
            message: `Definition is ${bytes} B; low-end devices parse and hash slowly above 512 KiB`,
        });
    }
    // ---- Pass 1: structural -------------------------------------------------
    const parsed = FormDefinitionSchema.safeParse(def);
    if (!parsed.success) {
        for (const issue of mapZodIssues(parsed.error.issues, def)) {
            (issue.severity === 'warning' ? warnings : errors).push(issue.issue);
        }
        // A structurally invalid document is not linked (docs/04 §15). Unknown
        // properties are only warnings, so they do not stop the semantic pass.
        if (errors.length > 0)
            return { ok: false, errors, warnings };
    }
    // ---- Pass 2: semantic ---------------------------------------------------
    runSemanticPass(def, opts, limits, errors, warnings);
    if (opts.previous)
        checkPublishRules(def, opts.previous, opts, errors, warnings);
    return { ok: errors.length === 0, errors, warnings };
}
// ---------------------------------------------------------------------------
// Issue plumbing
// ---------------------------------------------------------------------------
function ptr(...segments) {
    return segments
        .map((s) => `/${String(s).replaceAll('~', '~0').replaceAll('/', '~1')}`)
        .join('');
}
function resolveLimits(overrides) {
    const out = { ...DEFAULT_VALIDATION_LIMITS };
    if (!overrides)
        return out;
    for (const key of Object.keys(out)) {
        const value = overrides[key];
        // Hosts may tighten limits, never loosen them (docs/04 §16).
        if (typeof value === 'number' && Number.isFinite(value) && value < out[key])
            out[key] = value;
    }
    return out;
}
function safeStringify(value) {
    try {
        return JSON.stringify(value) ?? '';
    }
    catch {
        return '';
    }
}
function utf8ByteLength(input) {
    const Encoder = globalThis.TextEncoder;
    if (Encoder)
        return new Encoder().encode(input).length;
    // Fallback for runtimes without TextEncoder: count UTF-8 bytes by code point.
    let bytes = 0;
    for (const ch of input) {
        const cp = ch.codePointAt(0) ?? 0;
        bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
}
function findForbiddenKeys(node, path, errors) {
    if (node === null || typeof node !== 'object')
        return;
    if (Array.isArray(node)) {
        node.forEach((item, i) => findForbiddenKeys(item, `${path}${ptr(i)}`, errors));
        return;
    }
    for (const key of Object.keys(node)) {
        const child = `${path}${ptr(key)}`;
        if (FORBIDDEN_KEYS.includes(key)) {
            errors.push({
                code: 'E_PROTO_KEY',
                path: child,
                message: `Forbidden key "${key}" at ${path === '' ? '/' : path}`,
                hint: 'Prototype-pollution keys are rejected everywhere in a definition',
            });
            continue;
        }
        findForbiddenKeys(node[key], child, errors);
    }
}
function zodPath(path) {
    return path.map((p) => ptr(p)).join('');
}
function valueAt(root, path) {
    let node = root;
    for (const segment of path) {
        if (node === null || typeof node !== 'object')
            return undefined;
        node = node[segment];
    }
    return node;
}
/**
 * Flattens zod issues into catalogue codes. Union errors are the interesting case:
 * the element union is `discriminatedUnion(built-ins) | x:custom`, so when every
 * branch only complains about `type` the real problem is the type itself
 * (`E_UNKNOWN_TYPE` / `E_CUSTOM_TYPE_NAME`); otherwise the branch that got past the
 * discriminator carries the useful message.
 */
function mapZodIssues(issues, root) {
    const out = [];
    for (const issue of issues)
        out.push(...mapZodIssue(issue, root));
    return dedupe(out);
}
function dedupe(issues) {
    const seen = new Set();
    const out = [];
    for (const entry of issues) {
        const key = `${entry.issue.code}\u0000${entry.issue.path}\u0000${entry.issue.message}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(entry);
    }
    return out;
}
function mapZodIssue(issue, root) {
    if (issue.code === 'invalid_union') {
        const branches = issue.unionErrors.map((e) => e.issues);
        const basePath = issue.path;
        const discriminatorPath = [...basePath, 'type'];
        const isTypeOnly = (branch) => branch.length > 0 && branch.every((i) => samePath(i.path, discriminatorPath));
        if (branches.length > 0 && branches.every(isTypeOnly)) {
            const raw = valueAt(root, discriminatorPath);
            const shown = typeof raw === 'string' ? raw : JSON.stringify(raw);
            const custom = typeof raw === 'string' && raw.startsWith('x:');
            return [
                {
                    severity: 'error',
                    issue: {
                        code: custom ? 'E_CUSTOM_TYPE_NAME' : 'E_UNKNOWN_TYPE',
                        path: zodPath(discriminatorPath),
                        message: custom
                            ? `Element type ${shown} must match ^x:[a-z][a-z0-9-]*$`
                            : `Element type ${shown} is not a built-in type`,
                        hint: custom
                            ? 'Custom types are "x:" followed by a kebab-case name'
                            : `Built-in types: ${BUILT_IN_ELEMENT_TYPES.join(', ')}, or "x:<name>" for a host element`,
                    },
                },
            ];
        }
        const candidates = branches.filter((branch) => !isTypeOnly(branch) && branch.length > 0);
        const chosen = (candidates.length > 0 ? candidates : branches)
            .slice()
            .sort((a, b) => a.length - b.length)[0];
        if (!chosen || chosen.length === 0) {
            return [
                {
                    severity: 'error',
                    issue: { code: 'E_SCHEMA', path: zodPath(basePath), message: issue.message },
                },
            ];
        }
        return chosen.flatMap((i) => mapZodIssue(i, root));
    }
    const path = zodPath(issue.path);
    if (issue.code === 'unrecognized_keys') {
        // docs/04 §17: consumers ignore-and-preserve unknown properties within a MAJOR.
        return issue.keys.map((key) => ({
            severity: 'warning',
            issue: {
                code: 'W_UNKNOWN_PROPERTY',
                path: `${path}${ptr(key)}`,
                message: `Unknown property "${key}" is preserved but ignored`,
                hint: 'A newer 1.x minor, or a typo',
            },
        }));
    }
    const last = issue.path[issue.path.length - 1];
    if (issue.path.length === 1 && last === 'rasd') {
        const raw = valueAt(root, issue.path);
        const major = typeof raw === 'string' ? /^(\d+)\./.exec(raw)?.[1] : undefined;
        if (major !== undefined && major !== '1') {
            return [
                {
                    severity: 'error',
                    issue: {
                        code: 'E_UNSUPPORTED_RASD_MAJOR',
                        path,
                        message: `rasd ${String(raw)} is not supported by this client (supports 1.x)`,
                    },
                },
            ];
        }
    }
    if (last === 'name' && issue.code === 'invalid_string') {
        return [
            {
                severity: 'error',
                issue: {
                    code: 'E_INVALID_NAME',
                    path,
                    message: `Name ${JSON.stringify(valueAt(root, issue.path))} must match ^[a-zA-Z_][a-zA-Z0-9_]*$ (≤ 64 chars)`,
                },
            },
        ];
    }
    // docs/04 §10.12 gives `consent` its own code for a missing statement. The
    // semantic pass raises it when `props` is absent altogether, but it could not
    // when `props` was present and half-filled: `ConsentPropsSchema` requires both
    // keys, so zod rejected first and the author got
    // `E_TYPE_PROPS_INVALID /props/text Required` — a different code from the one
    // §10.12 documents, for the same mistake, depending only on whether they had
    // typed `"props": {}` yet. Mapping the issue here makes both paths report what
    // the spec says, without loosening the schema: a *valid* consent does have
    // text, every renderer relies on that, and the runtime type should keep saying
    // so.
    if ((last === 'text' || last === 'textVersion') &&
        issue.path.length >= 3 &&
        issue.path[issue.path.length - 2] === 'props') {
        const owner = valueAt(root, issue.path.slice(0, -2));
        if (owner?.type === 'consent') {
            return [
                {
                    severity: 'error',
                    issue: {
                        code: 'E_CONSENT_TEXT_MISSING',
                        path,
                        message: `consent "${String(owner.name)}" needs props.text and props.textVersion`,
                    },
                },
            ];
        }
    }
    // docs/04 §9: wrong types inside `props` are E_TYPE_PROPS_INVALID.
    if (issue.path.includes('props')) {
        return [{ severity: 'error', issue: { code: 'E_TYPE_PROPS_INVALID', path, message: issue.message } }];
    }
    return [{ severity: 'error', issue: { code: 'E_SCHEMA', path, message: issue.message } }];
}
function samePath(a, b) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}
const KEYWORDS = new Set(['and', 'or', 'not', 'mod', 'div', 'true', 'false', 'null']);
const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;
const SEGMENT_RE = /^[A-Za-z_][A-Za-z0-9_]*(\[\d*\])?$/;
function scanExpression(src) {
    const result = { refs: [], calls: [], bareIdentifiers: [], errors: [] };
    const stack = [];
    let i = 0;
    if (src.trim() === '') {
        result.errors.push({ message: 'Expression is empty', offset: 0 });
        return result;
    }
    while (i < src.length) {
        const c = src[i];
        if (c === "'" || c === '"') {
            // REL string literals have no escape sequences (docs/05 §2).
            const end = src.indexOf(c, i + 1);
            if (end === -1) {
                result.errors.push({ message: `Unterminated string literal (${c})`, offset: i });
                return result;
            }
            i = end + 1;
            continue;
        }
        if (c === '$' && src[i + 1] === '{') {
            const end = src.indexOf('}', i + 2);
            if (end === -1) {
                result.errors.push({ message: 'Unterminated ${…} reference', offset: i });
                return result;
            }
            const inner = src.slice(i + 2, end);
            const ref = parseRefPath(inner, i);
            if ('message' in ref)
                result.errors.push(ref);
            else
                result.refs.push(ref);
            i = end + 1;
            continue;
        }
        if (c === '(' || c === '[') {
            stack.push({ char: c, offset: i });
            i += 1;
            continue;
        }
        if (c === ')' || c === ']') {
            const open = stack.pop();
            const expected = c === ')' ? '(' : '[';
            if (!open || open.char !== expected) {
                result.errors.push({ message: `Unbalanced "${c}"`, offset: i });
                return result;
            }
            i += 1;
            continue;
        }
        if (c === '}') {
            result.errors.push({ message: 'Unexpected "}"', offset: i });
            return result;
        }
        if (IDENT_START.test(c)) {
            let j = i;
            while (j < src.length && IDENT_PART.test(src[j]))
                j += 1;
            const ident = src.slice(i, j);
            // Hyphenated names are legal only immediately before "(" (docs/05 §2).
            const hyphenated = /^(-[A-Za-z_][A-Za-z0-9_]*)+\(/.exec(src.slice(j));
            if (hyphenated) {
                result.calls.push(ident + hyphenated[0].slice(0, -1));
                i = j + hyphenated[0].length - 1;
                continue;
            }
            let k = j;
            while (k < src.length && /\s/.test(src[k]))
                k += 1;
            // `and (`, `or (`, `not (` are operators followed by a parenthesised group,
            // never calls — only a non-keyword identifier in front of `(` is a call.
            if (src[k] === '(' && (k === j || !KEYWORDS.has(ident))) {
                result.calls.push(ident);
                i = j;
                continue;
            }
            const memberAccess = i > 0 && src[i - 1] === '.';
            if (!memberAccess && !KEYWORDS.has(ident))
                result.bareIdentifiers.push(ident);
            i = j;
            continue;
        }
        i += 1;
    }
    const unclosed = stack.pop();
    if (unclosed)
        result.errors.push({ message: `Unclosed "${unclosed.char}"`, offset: unclosed.offset });
    return result;
}
function parseRefPath(inner, offset) {
    if (inner === '' || /\s/.test(inner)) {
        return { message: `Invalid reference \${${inner}} (no whitespace allowed inside \${…})`, offset };
    }
    let rest = inner;
    let up = 0;
    let root = false;
    if (rest.startsWith('/')) {
        root = true;
        rest = rest.slice(1);
    }
    else {
        while (rest.startsWith('../')) {
            up += 1;
            rest = rest.slice(3);
        }
    }
    if (rest === '..') {
        return { raw: inner, up, root, meta: false, segments: [], parentInstance: true, offset };
    }
    const parts = rest.split('.');
    if (parts.length === 0 || parts.some((p) => p === '')) {
        return { message: `Invalid reference \${${inner}}`, offset };
    }
    if (parts[0] === 'meta') {
        if (parts.length < 2)
            return { message: 'Invalid reference ${meta} (expected ${meta.<key>})', offset };
        return { raw: inner, up, root, meta: true, segments: parts.slice(1), parentInstance: false, offset };
    }
    const segments = [];
    for (const part of parts) {
        if (!SEGMENT_RE.test(part))
            return { message: `Invalid reference segment "${part}" in \${${inner}}`, offset };
        segments.push(part.replace(/\[\d*\]$/, ''));
    }
    return { raw: inner, up, root, meta: false, segments, parentInstance: false, offset };
}
/** Best-effort extraction of `pulldata('ds', 'col', 'keyCol', …)` literal arguments. */
const PULLDATA_RE = /pulldata\(\s*(['"])([^'"]*)\1\s*,\s*(['"])([^'"]*)\3\s*,\s*(['"])([^'"]*)\5/g;
function newScope(parent, repeatName) {
    return { parent, repeatName, decls: new Map() };
}
// ---------------------------------------------------------------------------
// Semantic pass
// ---------------------------------------------------------------------------
function runSemanticPass(def, opts, limits, errors, warnings) {
    const settings = def.settings ?? { defaultLocale: 'en', locales: ['en'] };
    const locales = Array.isArray(settings.locales) ? settings.locales : [];
    const ctx = {
        def,
        opts,
        limits,
        errors,
        warnings,
        locales,
        defaultLocale: typeof settings.defaultLocale === 'string' ? settings.defaultLocale : (locales[0] ?? 'en'),
        rootScope: newScope(null, null),
        decls: [],
        elements: [],
        exprSites: [],
        choiceLists: new Map(Object.entries(def.choiceLists ?? {})),
        usedLists: new Set(),
        datasets: new Map((def.datasets ?? []).map((d) => [d.name, d])),
        usedDatasets: new Set(),
        pageIds: new Set(),
        knownFunctions: buildFunctionSet(def, opts),
        translationSeverity: opts.translations ?? 'warn',
    };
    checkSpecVersion(ctx);
    checkSettings(ctx);
    collectPages(ctx);
    collectCalculated(ctx);
    checkNames(ctx);
    checkElements(ctx);
    checkChoiceListsAndDatasets(ctx);
    checkTriggers(ctx);
    checkCycles(ctx, checkExpressions(ctx));
    checkUnused(ctx);
}
function err(ctx, code, path, message, hint) {
    ctx.errors.push(hint === undefined ? { code, path, message } : { code, path, message, hint });
}
function warn(ctx, code, path, message, hint) {
    ctx.warnings.push(hint === undefined ? { code, path, message } : { code, path, message, hint });
}
function buildFunctionSet(def, opts) {
    const set = new Set(CORE_FUNCTIONS);
    for (const fn of opts.functions ?? [])
        set.add(fn);
    for (const feature of def.requires?.features ?? []) {
        if (typeof feature === 'string' && feature.startsWith('fn:'))
            set.add(feature.slice(3));
    }
    return set;
}
function checkSpecVersion(ctx) {
    const rasd = ctx.def.rasd;
    const match = typeof rasd === 'string' ? /^(\d+)\.(\d+)$/.exec(rasd) : null;
    if (match) {
        if (match[1] !== '1') {
            err(ctx, 'E_UNSUPPORTED_RASD_MAJOR', ptr('rasd'), `rasd ${rasd} is not supported by this client (supports 1.x)`);
        }
        else if (Number(match[2]) > 0) {
            warn(ctx, 'W_NEWER_MINOR', ptr('rasd'), `Document declares rasd ${rasd}; this client implements 1.0 — degraded features are possible`);
        }
    }
    const features = ctx.opts.features;
    if (features) {
        const supported = new Set(features);
        (ctx.def.requires?.features ?? []).forEach((feature, i) => {
            if (!supported.has(feature)) {
                err(ctx, 'E_REQUIRES_UNMET', ptr('requires', 'features', i), `Form requires ${feature}, which this client lacks`);
            }
        });
    }
}
function checkSettings(ctx) {
    const s = ctx.def.settings;
    if (!s)
        return;
    const base = ptr('settings');
    if (ctx.locales.length === 0) {
        err(ctx, 'E_LOCALE_INVALID', `${base}${ptr('locales')}`, 'At least one locale must be declared');
    }
    else if (!ctx.locales.includes(ctx.defaultLocale)) {
        err(ctx, 'E_DEFAULT_LOCALE_NOT_IN_LOCALES', `${base}${ptr('defaultLocale')}`, `defaultLocale "${ctx.defaultLocale}" is not a member of settings.locales`);
    }
    if (ctx.locales.length > 10) {
        warn(ctx, 'W_MANY_LOCALES', `${base}${ptr('locales')}`, `${ctx.locales.length} locales; more than 10 is unusual`);
    }
    const dupLocales = ctx.locales.filter((l, i) => ctx.locales.indexOf(l) !== i);
    for (const l of dupLocales) {
        err(ctx, 'E_LOCALE_INVALID', `${base}${ptr('locales')}`, `Locale "${l}" is listed more than once`);
    }
    if (s.encryption && s.encryption.mode !== 'none' && !s.encryption.publicKeyId) {
        err(ctx, 'E_ENCRYPTION_KEY_MISSING', `${base}${ptr('encryption', 'publicKeyId')}`, `settings.encryption.mode "${s.encryption.mode}" requires publicKeyId`);
    }
    localized(ctx, ctx.def.meta?.title, ptr('meta', 'title'));
    localized(ctx, ctx.def.meta?.description, ptr('meta', 'description'));
    localized(ctx, ctx.def.meta?.changelog, ptr('meta', 'changelog'));
    if (typeof s.instanceName === 'string') {
        ctx.exprSites.push({
            src: s.instanceName,
            path: `${base}${ptr('instanceName')}`,
            scope: ctx.rootScope,
            allowColumns: false,
            owner: null,
            edge: null,
        });
    }
}
// ---- declaration collection ------------------------------------------------
function collectPages(ctx) {
    const pages = ctx.def.pages;
    if (!Array.isArray(pages) || pages.length === 0) {
        err(ctx, 'E_NO_PAGES', ptr('pages'), 'A form must declare at least one page');
        return;
    }
    if (pages.length > ctx.limits.pages) {
        err(ctx, 'E_TOO_MANY_PAGES', ptr('pages'), `${pages.length} pages; limit ${ctx.limits.pages}`);
    }
    else if (pages.length > 200) {
        warn(ctx, 'W_MANY_PAGES', ptr('pages'), `${pages.length} pages; more than 200 hurts navigation`);
    }
    pages.forEach((page, i) => {
        const path = ptr('pages', i);
        if (ctx.pageIds.has(page.id)) {
            err(ctx, 'E_PAGE_ID_DUPLICATE', `${path}${ptr('id')}`, `Duplicate page id "${page.id}"`);
        }
        ctx.pageIds.add(page.id);
        localized(ctx, page.title, `${path}${ptr('title')}`);
        localized(ctx, page.description, `${path}${ptr('description')}`);
        if (typeof page.relevant === 'string') {
            ctx.exprSites.push({
                src: page.relevant,
                path: `${path}${ptr('relevant')}`,
                scope: ctx.rootScope,
                allowColumns: false,
                owner: null,
                edge: null,
            });
        }
        const elements = Array.isArray(page.elements) ? page.elements : [];
        if (elements.length === 0)
            warn(ctx, 'W_EMPTY_PAGE', path, `Page "${page.id}" has no elements`);
        collectElements(ctx, elements, `${path}${ptr('elements')}`, ctx.rootScope, 0, 0);
    });
    if (ctx.elements.length > ctx.limits.elements) {
        err(ctx, 'E_TOO_MANY_ELEMENTS', ptr('pages'), `${ctx.elements.length} elements; limit ${ctx.limits.elements}`);
    }
}
function collectElements(ctx, elements, basePath, scope, containerDepth, repeatDepth) {
    elements.forEach((el, i) => {
        const path = `${basePath}${ptr(i)}`;
        if (!el || typeof el !== 'object' || typeof el.type !== 'string' || typeof el.name !== 'string') {
            return; // the structural pass already reported this
        }
        const isContainer = CONTAINER_TYPES.has(el.type);
        const decl = declare(ctx, el.name, el.type, path, scope, {
            hasCalculate: typeof el.calculate === 'string' && el.calculate.length > 0,
            sensitive: el.bind?.sensitive === true,
        });
        const record = { el, path, scope, decl, containerDepth, repeatDepth };
        ctx.elements.push(record);
        const children = el.elements;
        if (!isContainer && Array.isArray(children)) {
            err(ctx, 'E_ELEMENTS_ON_LEAF', `${path}${ptr('elements')}`, `"elements" is only allowed on group/repeat, not ${el.type}`);
        }
        if (isContainer) {
            const nextContainerDepth = containerDepth + 1;
            if (nextContainerDepth > ctx.limits.containerDepth) {
                err(ctx, 'E_NESTING_TOO_DEEP', path, `Container nesting is ${nextContainerDepth} levels; limit ${ctx.limits.containerDepth}`);
            }
            if (el.type === 'repeat') {
                const nextRepeatDepth = repeatDepth + 1;
                if (nextRepeatDepth > ctx.limits.repeatNesting) {
                    err(ctx, 'E_REPEAT_NESTING', path, `Repeats nest ${nextRepeatDepth} deep; limit ${ctx.limits.repeatNesting}`);
                }
                const childScope = newScope(scope, el.name);
                if (decl)
                    decl.childScope = childScope;
                collectElements(ctx, children ?? [], `${path}${ptr('elements')}`, childScope, nextContainerDepth, nextRepeatDepth);
            }
            else {
                // Groups are transparent: children share the enclosing name scope.
                collectElements(ctx, children ?? [], `${path}${ptr('elements')}`, scope, nextContainerDepth, repeatDepth);
            }
        }
        // `<name>_other` is created by the engine for select questions with an Other option.
        const other = el.props?.other;
        if (other?.enabled === true) {
            declare(ctx, `${el.name}_other`, 'text', path, scope, { hasCalculate: false, sensitive: false, implicit: true });
        }
    });
}
function declare(ctx, name, type, path, scope, opts) {
    const key = name.toLowerCase();
    const existing = scope.decls.get(key);
    if (existing) {
        // Either order counts: the authored element may come before or after the
        // select question whose `other` option creates `<name>_other`.
        if (existing.implicit !== (opts.implicit === true)) {
            err(ctx, 'E_IMPLICIT_KEY_COLLISION', `${(opts.implicit === true ? existing.path : path)}${ptr('name')}`, `Name "${name}" collides with the implicit "Other" key of a select question`);
        }
        else if (!opts.implicit) {
            err(ctx, 'E_DUPLICATE_NAME', `${path}${ptr('name')}`, `Name "${name}" is already used in this scope (names must be unique case-insensitively)`);
        }
        return null;
    }
    const decl = {
        id: ctx.decls.length,
        name,
        type,
        path,
        scope,
        childScope: null,
        hasCalculate: opts.hasCalculate,
        implicit: opts.implicit === true,
        sensitive: opts.sensitive,
    };
    scope.decls.set(key, decl);
    ctx.decls.push(decl);
    return decl;
}
function collectCalculated(ctx) {
    const calculated = ctx.def.logic?.calculated ?? [];
    if (calculated.length > 500) {
        warn(ctx, 'W_MANY_CALCULATED', ptr('logic', 'calculated'), `${calculated.length} calculated values; more than 500 is unusual`);
    }
    calculated.forEach((entry, i) => {
        const path = ptr('logic', 'calculated', i);
        if (!entry || typeof entry.name !== 'string')
            return;
        const decl = declare(ctx, entry.name, 'calculate', path, ctx.rootScope, {
            hasCalculate: typeof entry.calculate === 'string',
            sensitive: false,
        });
        if (typeof entry.calculate === 'string') {
            ctx.exprSites.push({
                src: entry.calculate,
                path: `${path}${ptr('calculate')}`,
                scope: ctx.rootScope,
                allowColumns: false,
                owner: decl,
                edge: 'calculate',
            });
        }
    });
}
// ---- names -----------------------------------------------------------------
function checkNames(ctx) {
    for (const decl of ctx.decls) {
        if (decl.implicit)
            continue;
        const namePath = `${decl.path}${ptr('name')}`;
        if (RESERVED_NAMES.includes(decl.name) || decl.name.startsWith('__')) {
            err(ctx, 'E_RESERVED_NAME', namePath, `Name "${decl.name}" is reserved (docs/04 §13)`, 'Reserved keys collide with submission metadata or Kobo/ODK export columns');
        }
        else if (decl.name.startsWith('_')) {
            warn(ctx, 'W_UNDERSCORE_NAME', namePath, `Name "${decl.name}" starts with an underscore and may collide with Kobo/Ona export columns`);
        }
        // Shadowing an outer name makes ${name} ambiguous for readers of the data.
        if (decl.scope.parent) {
            for (let s = decl.scope.parent; s !== null; s = s.parent) {
                const outer = s.decls.get(decl.name.toLowerCase());
                if (outer) {
                    err(ctx, 'E_NAME_SHADOWED', namePath, `Name "${decl.name}" shadows an outer element of the same name`);
                    break;
                }
            }
        }
    }
}
// ---- elements --------------------------------------------------------------
function checkElements(ctx) {
    const encryptionMode = ctx.def.settings?.encryption?.mode ?? 'none';
    for (const record of ctx.elements) {
        const { el, path, scope, decl } = record;
        const props = el.props ?? {};
        // labels and localized strings
        if (!VALUE_LESS_TYPES.has(el.type) && !CONTAINER_TYPES.has(el.type) && el.type !== 'hidden' && el.type !== 'calculate') {
            if (el.label === undefined) {
                warn(ctx, 'W_LABEL_MISSING', path, `Question "${el.name}" has no label`, 'Screen readers announce the label');
            }
        }
        if (el.type === 'note' && el.label === undefined) {
            warn(ctx, 'W_LABEL_MISSING', path, `Note "${el.name}" has no label (the note body)`);
        }
        localized(ctx, el.label, `${path}${ptr('label')}`);
        localized(ctx, el.hint, `${path}${ptr('hint')}`);
        localized(ctx, el.guidance, `${path}${ptr('guidance')}`);
        localized(ctx, el.requiredMessage, `${path}${ptr('requiredMessage')}`);
        localized(ctx, el.constraintMessage, `${path}${ptr('constraintMessage')}`);
        // per-type structural semantics
        if (el.type === 'calculate' && typeof el.calculate !== 'string') {
            err(ctx, 'E_CALCULATE_MISSING', path, `calculate element "${el.name}" has no calculate expression`);
        }
        if (el.type === 'calculate' && el.default !== undefined) {
            err(ctx, 'E_DEFAULT_ON_CALCULATE', `${path}${ptr('default')}`, `calculate element "${el.name}" cannot have a default`);
        }
        if (el.type === 'note') {
            const offenders = ['required', 'constraint', 'default', 'calculate'].filter((k) => el[k] !== undefined);
            if (offenders.length > 0) {
                err(ctx, 'E_NOTE_WITH_VALUE_PROPS', path, `note "${el.name}" stores no value, so ${offenders.join(', ')} ${offenders.length === 1 ? 'is' : 'are'} not allowed`);
            }
        }
        if (el.type === 'hidden' && el.required !== undefined && el.required !== false) {
            warn(ctx, 'W_REQUIRED_HIDDEN', `${path}${ptr('required')}`, `hidden element "${el.name}" can never be filled by a user`);
        }
        if (el.type === 'group' && el.required !== undefined) {
            warn(ctx, 'W_REQUIRED_ON_GROUP', `${path}${ptr('required')}`, `required is ignored on group "${el.name}"`);
        }
        if (el.type === 'matrix') {
            const rows = props['rows'];
            if (!Array.isArray(rows) || rows.length === 0) {
                err(ctx, 'E_MATRIX_ROWS_EMPTY', `${path}${ptr('props', 'rows')}`, `matrix "${el.name}" has no rows`);
            }
            else {
                const seen = new Set();
                rows.forEach((row, i) => {
                    const value = row.value;
                    if (typeof value === 'string') {
                        if (seen.has(value)) {
                            err(ctx, 'E_CHOICE_DUPLICATE', `${path}${ptr('props', 'rows', i, 'value')}`, `Duplicate matrix row "${value}"`);
                        }
                        seen.add(value);
                    }
                    localized(ctx, row.label, `${path}${ptr('props', 'rows', i, 'label')}`);
                    const rowRelevant = row.relevant;
                    if (typeof rowRelevant === 'string') {
                        ctx.exprSites.push({
                            src: rowRelevant,
                            path: `${path}${ptr('props', 'rows', i, 'relevant')}`,
                            scope,
                            allowColumns: false,
                            owner: decl,
                            edge: null,
                        });
                    }
                });
            }
            const columns = props['columns'];
            if (columns?.type === 'select_one')
                checkChoiceSource(ctx, el, columns, `${path}${ptr('props', 'columns')}`);
        }
        if (el.type === 'consent') {
            // Only the no-props-at-all case is left here. When `props` IS present but
            // incomplete, `ConsentPropsSchema` rejects it first and `mapZodIssue`
            // turns that into the same E_CONSENT_TEXT_MISSING with a pointer at the
            // exact missing key; checking again here would report it twice. When
            // `props` is absent entirely, zod has nothing to complain about — the
            // whole object is optional — so this is the only thing standing between a
            // consent element with no statement and a clean bill of health.
            if (el.props === undefined) {
                err(ctx, 'E_CONSENT_TEXT_MISSING', `${path}${ptr('props')}`, `consent "${el.name}" needs props.text and props.textVersion`);
            }
            localized(ctx, props['text'], `${path}${ptr('props', 'text')}`);
        }
        if (SELECT_TYPES.has(el.type)) {
            checkChoiceSource(ctx, el, props, `${path}${ptr('props')}`);
            localized(ctx, props['other']?.label, `${path}${ptr('props', 'other', 'label')}`);
        }
        if (el.type === 'repeat') {
            const min = props['min'];
            const max = props['max'];
            if (typeof min === 'number' && typeof max === 'number' && min > max) {
                err(ctx, 'E_REPEAT_MIN_GT_MAX', `${path}${ptr('props')}`, `repeat "${el.name}": min ${min} is greater than max ${max}`);
            }
            if (typeof max === 'number' && max > 500) {
                warn(ctx, 'W_REPEAT_MAX_LARGE', `${path}${ptr('props', 'max')}`, `repeat "${el.name}" allows ${max} instances; memory on low-end devices`);
            }
            if (props['count'] !== undefined && (min !== undefined || max !== undefined)) {
                warn(ctx, 'W_REPEAT_COUNT_WITH_MIN_MAX', `${path}${ptr('props', 'count')}`, `repeat "${el.name}" sets count together with min/max; count wins and the add/remove controls are hidden`);
            }
            const keyField = props['keyField'];
            if (typeof keyField === 'string' && decl?.childScope && !decl.childScope.decls.has(keyField.toLowerCase())) {
                err(ctx, 'E_EXPR_UNKNOWN_REF', `${path}${ptr('props', 'keyField')}`, `repeat "${el.name}": keyField "${keyField}" is not a child element`);
            }
            localized(ctx, props['addLabel'], `${path}${ptr('props', 'addLabel')}`);
            localized(ctx, props['removeLabel'], `${path}${ptr('props', 'removeLabel')}`);
        }
        localized(ctx, props['placeholder'], `${path}${ptr('props', 'placeholder')}`);
        localized(ctx, props['unit'], `${path}${ptr('props', 'unit')}`);
        // custom element registration
        if (el.type.startsWith('x:')) {
            if (!CUSTOM_TYPE_PATTERN.test(el.type)) {
                err(ctx, 'E_CUSTOM_TYPE_NAME', `${path}${ptr('type')}`, `Custom type "${el.type}" must match ^x:[a-z][a-z0-9-]*$`);
            }
            else if (!(ctx.opts.features ?? []).includes(el.type)) {
                warn(ctx, 'W_UNREGISTERED_CUSTOM_TYPE', `${path}${ptr('type')}`, `Custom type "${el.type}" is not registered; it renders as a placeholder`, `Pass it in features, or declare requires.features ["${el.type}"] to refuse old clients`);
            }
        }
        // data-protection hygiene
        if (el.bind?.sensitive === true && encryptionMode === 'none') {
            warn(ctx, 'W_SENSITIVE_WITHOUT_ENCRYPTION', `${path}${ptr('bind', 'sensitive')}`, `"${el.name}" is marked sensitive but settings.encryption.mode is "none"`);
        }
        if (el.bind?.sensitive === true && el.bind.index === true) {
            warn(ctx, 'W_INDEX_ON_SENSITIVE', `${path}${ptr('bind', 'index')}`, `"${el.name}" is indexed and sensitive; index columns stay cleartext under encryption`);
        }
        // validators
        const validators = el.validators ?? [];
        if (validators.length > ctx.limits.validatorsPerElement) {
            err(ctx, 'E_TOO_MANY_VALIDATORS', `${path}${ptr('validators')}`, `${validators.length} validators on "${el.name}"; limit ${ctx.limits.validatorsPerElement}`);
        }
        validators.forEach((validator, i) => {
            const vPath = `${path}${ptr('validators', i)}`;
            localized(ctx, validator.message, `${vPath}${ptr('message')}`);
            if (validator.type === 'expr') {
                ctx.exprSites.push({ src: validator.expr, path: `${vPath}${ptr('expr')}`, scope, allowColumns: false, owner: decl, edge: null });
            }
            if (validator.type === 'regex') {
                try {
                    new RegExp(validator.pattern, 'u');
                }
                catch (e) {
                    err(ctx, 'E_SCHEMA', `${vPath}${ptr('pattern')}`, `Invalid regular expression: ${e.message}`);
                }
            }
            if (validator.type === 'custom' && !(ctx.opts.features ?? []).includes(`cap:validator:${validator.id}`)) {
                warn(ctx, 'W_CUSTOM_VALIDATOR_UNKNOWN', `${vPath}${ptr('id')}`, `Custom validator "${validator.id}" must be registered by the host; it passes otherwise`);
            }
        });
        // expression-bearing properties (docs/05 §9)
        pushExpr(ctx, el.relevant, `${path}${ptr('relevant')}`, scope, decl, 'relevant');
        pushExpr(ctx, el.calculate, `${path}${ptr('calculate')}`, scope, decl, 'calculate');
        pushExpr(ctx, el.constraint, `${path}${ptr('constraint')}`, scope, decl, null);
        if (typeof el.required === 'string')
            pushExpr(ctx, el.required, `${path}${ptr('required')}`, scope, decl, null);
        if (typeof el.readonly === 'string')
            pushExpr(ctx, el.readonly, `${path}${ptr('readonly')}`, scope, decl, null);
        if (el.default && 'expr' in el.default) {
            pushExpr(ctx, el.default.expr, `${path}${ptr('default', 'expr')}`, scope, decl, null);
        }
        if (typeof props['choiceFilter'] === 'string') {
            ctx.exprSites.push({
                src: props['choiceFilter'],
                path: `${path}${ptr('props', 'choiceFilter')}`,
                scope,
                allowColumns: true,
                owner: decl,
                edge: null,
            });
        }
        if (typeof props['count'] === 'string') {
            // `count` decides how many instances exist, so it is evaluated outside the repeat.
            pushExpr(ctx, props['count'], `${path}${ptr('props', 'count')}`, scope, decl, null);
        }
        if (typeof props['itemLabel'] === 'string' && decl?.childScope) {
            pushExpr(ctx, props['itemLabel'], `${path}${ptr('props', 'itemLabel')}`, decl.childScope, decl, null);
        }
    }
}
function pushExpr(ctx, src, path, scope, owner, edge) {
    if (typeof src !== 'string')
        return;
    ctx.exprSites.push({ src, path, scope, allowColumns: false, owner, edge });
}
function checkChoiceSource(ctx, el, source, path) {
    const hasList = typeof source.list === 'string';
    const hasChoices = Array.isArray(source.choices);
    if (hasList === hasChoices) {
        err(ctx, 'E_SELECT_SOURCE', path, `"${el.name}" must declare exactly one of props.list or props.choices`);
    }
    if (hasList) {
        const list = source.list;
        ctx.usedLists.add(list);
        if (!ctx.choiceLists.has(list)) {
            err(ctx, 'E_LIST_NOT_FOUND', `${path}${ptr('list')}`, `Choice list "${list}" is not declared in choiceLists`);
        }
    }
    if (hasChoices) {
        checkInlineChoices(ctx, source.choices, `${path}${ptr('choices')}`);
    }
}
function checkInlineChoices(ctx, choices, path) {
    if (choices.length > ctx.limits.inlineChoicesPerList) {
        err(ctx, 'E_INLINE_CHOICES_TOO_MANY', path, `${choices.length} inline choices; limit ${ctx.limits.inlineChoicesPerList}`);
    }
    else if (choices.length > 500) {
        warn(ctx, 'W_INLINE_CHOICES_LARGE', path, `${choices.length} inline choices; use a dataset above 500`);
    }
    const seen = new Set();
    choices.forEach((choice, i) => {
        if (!choice || typeof choice.value !== 'string')
            return;
        if (seen.has(choice.value)) {
            err(ctx, 'E_CHOICE_DUPLICATE', `${path}${ptr(i, 'value')}`, `Duplicate choice value "${choice.value}"`);
        }
        seen.add(choice.value);
        localized(ctx, choice.label, `${path}${ptr(i, 'label')}`);
    });
}
// ---- choice lists and datasets --------------------------------------------
function checkChoiceListsAndDatasets(ctx) {
    for (const [name, list] of ctx.choiceLists) {
        const path = ptr('choiceLists', name);
        const hasChoices = Array.isArray(list.choices);
        const hasSource = list.source !== undefined;
        if (hasChoices === hasSource) {
            err(ctx, 'E_CHOICELIST_SOURCE', path, `Choice list "${name}" must declare exactly one of choices or source`);
        }
        if (hasChoices)
            checkInlineChoices(ctx, list.choices, `${path}${ptr('choices')}`);
        if (hasSource) {
            const datasetName = list.source?.dataset;
            if (typeof datasetName === 'string') {
                ctx.usedDatasets.add(datasetName);
                const dataset = ctx.datasets.get(datasetName);
                if (!dataset) {
                    err(ctx, 'E_DATASET_NOT_FOUND', `${path}${ptr('source', 'dataset')}`, `Dataset "${datasetName}" is not declared`);
                }
                else {
                    const columns = declaredColumns(dataset);
                    if (list.valueKey === undefined || list.labelKey === undefined) {
                        err(ctx, 'E_CHOICELIST_SOURCE', path, `Dataset-backed list "${name}" needs valueKey and labelKey`);
                    }
                    for (const [key, prop] of [
                        [list.valueKey, 'valueKey'],
                        [list.labelKey, 'labelKey'],
                    ]) {
                        if (columns && typeof key === 'string' && !columns.has(key)) {
                            err(ctx, 'E_DATASET_COLUMN_UNKNOWN', `${path}${ptr(prop)}`, `Column "${key}" is not declared on dataset "${datasetName}"`);
                        }
                    }
                    (list.filterKeys ?? []).forEach((key, i) => {
                        if (columns && !columns.has(key)) {
                            warn(ctx, 'W_DATASET_COLUMN_UNKNOWN', `${path}${ptr('filterKeys', i)}`, `Filter column "${key}" is not declared on dataset "${datasetName}"`);
                        }
                    });
                }
            }
        }
    }
    (ctx.def.datasets ?? []).forEach((dataset, i) => {
        const path = ptr('datasets', i);
        const rows = dataset.inline;
        if (Array.isArray(rows)) {
            if (rows.length > ctx.limits.inlineDatasetRows) {
                err(ctx, 'E_DATASET_INLINE_TOO_MANY', `${path}${ptr('inline')}`, `${rows.length} inline rows; limit ${ctx.limits.inlineDatasetRows}`);
            }
            else if (rows.length > 2000) {
                warn(ctx, 'W_DATASET_INLINE_LARGE', `${path}${ptr('inline')}`, `${rows.length} inline rows; large tables belong on the server`);
            }
        }
        if (dataset.source === 'inline' && !Array.isArray(rows)) {
            err(ctx, 'E_SCHEMA', path, `Dataset "${dataset.name}" declares source "inline" but has no inline rows`);
        }
        if (dataset.source === 'url' && typeof dataset.url !== 'string') {
            err(ctx, 'E_SCHEMA', path, `Dataset "${dataset.name}" declares source "url" but has no url`);
        }
        const columns = declaredColumns(dataset);
        if (columns && !columns.has(dataset.keyField)) {
            err(ctx, 'E_DATASET_COLUMN_UNKNOWN', `${path}${ptr('keyField')}`, `keyField "${dataset.keyField}" is not among the declared columns of "${dataset.name}"`);
        }
        (dataset.columns ?? []).forEach((column, ci) => {
            localized(ctx, column.label, `${path}${ptr('columns', ci, 'label')}`);
        });
    });
    // choiceFilter columns must exist on the backing list (docs/04 §5).
    for (const record of ctx.elements) {
        const props = record.el.props ?? {};
        const filter = props['choiceFilter'];
        if (typeof filter !== 'string')
            continue;
        const listName = props['list'];
        const columns = typeof listName === 'string'
            ? listColumns(ctx, listName)
            : Array.isArray(props['choices'])
                ? inlineChoiceColumns(props['choices'])
                : null;
        if (!columns)
            continue;
        const scanned = scanExpression(filter);
        for (const ident of scanned.bareIdentifiers) {
            if (!columns.has(ident)) {
                warn(ctx, 'W_DATASET_COLUMN_UNKNOWN', `${record.path}${ptr('props', 'choiceFilter')}`, `choiceFilter references column "${ident}", which the choice source does not declare`);
            }
        }
    }
}
function declaredColumns(dataset) {
    if (!Array.isArray(dataset.columns) || dataset.columns.length === 0)
        return null;
    return new Set(dataset.columns.map((c) => c.name));
}
function inlineChoiceColumns(choices) {
    const columns = new Set(['value', 'label']);
    for (const choice of choices) {
        for (const key of Object.keys(choice?.attrs ?? {}))
            columns.add(key);
    }
    return columns;
}
function listColumns(ctx, listName) {
    const list = ctx.choiceLists.get(listName);
    if (!list)
        return null;
    if (Array.isArray(list.choices))
        return inlineChoiceColumns(list.choices);
    const dataset = list.source ? ctx.datasets.get(list.source.dataset) : undefined;
    if (!dataset)
        return null;
    const columns = declaredColumns(dataset);
    if (!columns)
        return null;
    for (const key of list.filterKeys ?? [])
        columns.add(key);
    return columns;
}
function checkUnused(ctx) {
    for (const name of ctx.choiceLists.keys()) {
        if (!ctx.usedLists.has(name)) {
            warn(ctx, 'W_UNUSED_CHOICE_LIST', ptr('choiceLists', name), `Choice list "${name}" is never referenced`);
        }
    }
    (ctx.def.datasets ?? []).forEach((dataset, i) => {
        if (!ctx.usedDatasets.has(dataset.name)) {
            warn(ctx, 'W_UNUSED_DATASET', ptr('datasets', i), `Dataset "${dataset.name}" is never referenced`);
        }
    });
}
// ---- triggers --------------------------------------------------------------
function checkTriggers(ctx) {
    const triggers = ctx.def.logic?.triggers ?? [];
    if (triggers.length > 200) {
        warn(ctx, 'W_MANY_TRIGGERS', ptr('logic', 'triggers'), `${triggers.length} triggers; more than 200 is unusual`);
    }
    const ids = new Set();
    triggers.forEach((trigger, i) => {
        const path = ptr('logic', 'triggers', i);
        if (ids.has(trigger.id)) {
            err(ctx, 'E_DUPLICATE_NAME', `${path}${ptr('id')}`, `Duplicate trigger id "${trigger.id}"`);
        }
        ids.add(trigger.id);
        pushExpr(ctx, trigger.when, `${path}${ptr('when')}`, ctx.rootScope, null, null);
        (trigger.actions ?? []).forEach((action, j) => {
            const aPath = `${path}${ptr('actions', j)}`;
            if (action.type === 'setValue' || action.type === 'clearValue') {
                const target = ctx.rootScope.decls.get(action.target?.toLowerCase() ?? '');
                if (!target) {
                    err(ctx, 'E_TRIGGER_TARGET_UNKNOWN', `${aPath}${ptr('target')}`, `Trigger target "${action.target}" is not a form-level question`);
                }
                else if (target.hasCalculate || target.type === 'calculate') {
                    err(ctx, 'E_TRIGGER_TARGET_CALCULATED', `${aPath}${ptr('target')}`, `Trigger target "${action.target}" is calculated; its value is owned by its expression`);
                }
            }
            if (action.type === 'setValue') {
                const hasValue = Object.hasOwn(action, 'value');
                const hasExpr = typeof action.expr === 'string';
                if (hasValue === hasExpr) {
                    err(ctx, 'E_SCHEMA', aPath, 'setValue needs exactly one of value or expr');
                }
                if (hasExpr)
                    pushExpr(ctx, action.expr, `${aPath}${ptr('expr')}`, ctx.rootScope, null, null);
            }
            if (action.type === 'skipTo' && !ctx.pageIds.has(action.page)) {
                err(ctx, 'E_SKIPTO_UNKNOWN_PAGE', `${aPath}${ptr('page')}`, `skipTo references unknown page "${action.page}"`);
            }
            if (action.type === 'showMessage')
                localized(ctx, action.message, `${aPath}${ptr('message')}`);
            if (action.type === 'complete')
                localized(ctx, action.message, `${aPath}${ptr('message')}`);
        });
    });
}
// ---- expressions -----------------------------------------------------------
function checkExpressions(ctx) {
    const graph = new Map();
    for (const site of ctx.exprSites) {
        if (site.src.length > ctx.limits.exprChars) {
            err(ctx, 'E_EXPR_TOO_LARGE', site.path, `Expression is ${site.src.length} chars; limit ${ctx.limits.exprChars}`);
            continue;
        }
        const scanned = scanExpression(site.src);
        for (const parseError of scanned.errors) {
            err(ctx, 'E_EXPR_PARSE', site.path, `Cannot parse expression at column ${parseError.offset + 1}: ${parseError.message}`);
        }
        if (scanned.errors.length > 0)
            continue;
        // The scan above is lexical: it finds unbalanced parentheses, unterminated
        // strings and bare identifiers, and it is what produces the `${…}` and call
        // lists the linker needs. It cannot see GRAMMAR. `1 +`, `1 2 3` and a bare
        // `and` are all sequences of perfectly good tokens, and every one of them
        // used to leave `rasd validate` reporting a clean document — the real parse
        // then failed at `createFormEngine()` time, which in this product means on
        // a phone, in a field office, with the enumerator holding it.
        //
        // So the definitive parser runs here too. It costs an authoring-time parse
        // of each expression and nothing at runtime: the engine parses the same
        // sources at compile time anyway, and `parseExpression` caches by source
        // text, so the second parse is a map lookup. Nothing else in this file may
        // depend on the AST — the scanner stays the source of names — but "does
        // this parse at all" is not a question a validator gets to leave open.
        try {
            parseExpression(site.src);
        }
        catch (e) {
            const offset = isRasdError(e) ? (e.details?.offset ?? 0) : 0;
            err(ctx, 'E_EXPR_PARSE', site.path, `Cannot parse expression at column ${offset + 1}: ${e.message}`);
            continue;
        }
        for (const call of scanned.calls) {
            if (!ctx.knownFunctions.has(call)) {
                err(ctx, 'E_EXPR_UNKNOWN_FUNCTION', site.path, `Unknown function ${call}()`, 'Register it with registerFunction(), pass it in options.functions, or declare requires.features ["fn:' + call + '"]');
            }
        }
        if (!site.allowColumns) {
            for (const ident of scanned.bareIdentifiers) {
                err(ctx, 'E_EXPR_PARSE', site.path, `Unexpected identifier "${ident}"`, 'Bare identifiers name choice columns and are legal only inside choiceFilter; use ${' + ident + '} for a field');
            }
        }
        for (const ref of scanned.refs) {
            const resolved = resolveRef(ctx, ref, site);
            if (resolved.kind === 'unknown') {
                err(ctx, 'E_EXPR_UNKNOWN_REF', site.path, resolved.message);
            }
            else if (resolved.kind === 'decl' && site.owner && site.edge) {
                addEdge(graph, site.owner.id, resolved.decl.id);
            }
        }
        // dataset lookups
        for (const match of site.src.matchAll(PULLDATA_RE)) {
            const datasetName = match[2];
            const column = match[4];
            const keyColumn = match[6];
            ctx.usedDatasets.add(datasetName);
            const dataset = ctx.datasets.get(datasetName);
            if (!dataset) {
                err(ctx, 'E_DATASET_NOT_FOUND', site.path, `pulldata() references unknown dataset "${datasetName}"`);
                continue;
            }
            const columns = declaredColumns(dataset);
            if (!columns)
                continue;
            for (const name of [column, keyColumn]) {
                if (!columns.has(name)) {
                    warn(ctx, 'W_DATASET_COLUMN_UNKNOWN', site.path, `pulldata() reads column "${name}", which dataset "${datasetName}" does not declare`);
                }
            }
        }
    }
    // instanceName must not leak PII (docs/04 §9, bind.sensitive)
    const instanceName = ctx.def.settings?.instanceName;
    if (typeof instanceName === 'string') {
        for (const ref of scanExpression(instanceName).refs) {
            const first = ref.segments[0];
            const decl = first ? ctx.rootScope.decls.get(first.toLowerCase()) : undefined;
            if (decl?.sensitive) {
                warn(ctx, 'W_SENSITIVE_IN_INSTANCE_NAME', ptr('settings', 'instanceName'), `instanceName includes the sensitive field "${decl.name}"; it is shown in submission lists`);
            }
        }
    }
    return graph;
}
function resolveRef(ctx, ref, site) {
    if (ref.meta) {
        const key = ref.segments[0];
        if (key === 'custom')
            return { kind: 'ok' };
        if (key !== undefined && META_KEYS.includes(key))
            return { kind: 'ok' };
        return { kind: 'unknown', message: `Expression references unknown metadata \${meta.${ref.segments.join('.')}}` };
    }
    if (ref.parentInstance)
        return { kind: 'ok' };
    let scope = ref.root ? ctx.rootScope : site.scope;
    for (let i = 0; i < ref.up && scope; i += 1)
        scope = scope.parent;
    if (!scope) {
        return { kind: 'unknown', message: `Expression \${${ref.raw}} climbs above the root scope` };
    }
    const first = ref.segments[0];
    if (first === undefined)
        return { kind: 'unknown', message: `Invalid reference \${${ref.raw}}` };
    let found;
    for (let s = scope; s !== null; s = ref.root ? null : s.parent) {
        found = s.decls.get(first.toLowerCase());
        if (found)
            break;
    }
    if (!found) {
        return { kind: 'unknown', message: `Expression references unknown field \${${ref.raw}}` };
    }
    // Only repeats own a child namespace; every other multi-segment reference walks
    // into an object value (${consent.granted}, ${fcs.cereals}) and is checked at runtime.
    if (ref.segments.length > 1 && found.childScope) {
        const child = ref.segments[1];
        const childDecl = found.childScope.decls.get(child.toLowerCase());
        if (!childDecl) {
            return {
                kind: 'unknown',
                message: `Expression references \${${ref.raw}}, but "${child}" is not a child of repeat "${found.name}"`,
            };
        }
        return { kind: 'decl', decl: childDecl };
    }
    return { kind: 'decl', decl: found };
}
function addEdge(graph, from, to) {
    let set = graph.get(from);
    if (!set) {
        set = new Set();
        graph.set(from, set);
    }
    set.add(to);
}
/**
 * Kahn/DFS cycle detection over `X:calculate → deps` and `X:relevant → deps` edges.
 * `X:value` depends on both, so a loop through either property is a real cycle
 * (docs/05 §9).
 */
function checkCycles(ctx, graph) {
    const state = new Map();
    const stack = [];
    const reported = new Set();
    const visit = (id) => {
        const current = state.get(id);
        if (current === 'done')
            return;
        if (current === 'visiting') {
            const start = stack.indexOf(id);
            const cycle = stack.slice(start).concat(id);
            const key = [...cycle].sort((a, b) => a - b).join(',');
            if (!reported.has(key)) {
                reported.add(key);
                const names = cycle.map((n) => ctx.decls[n]?.name ?? '?');
                const head = ctx.decls[cycle[0]];
                err(ctx, 'E_CALC_CYCLE', head?.path ?? '', `Calculation cycle: ${names.join(' → ')}`, 'Break the loop by computing one of these values from a user-entered field');
            }
            return;
        }
        state.set(id, 'visiting');
        stack.push(id);
        for (const next of graph.get(id) ?? [])
            visit(next);
        stack.pop();
        state.set(id, 'done');
    };
    for (const id of graph.keys())
        visit(id);
}
// ---- i18n ------------------------------------------------------------------
function localized(ctx, value, path) {
    if (value === undefined || ctx.locales.length < 2)
        return;
    const missing = [];
    if (typeof value === 'string') {
        // A plain string is authored in defaultLocale; every other locale falls back to it.
        for (const locale of ctx.locales)
            if (locale !== ctx.defaultLocale)
                missing.push(locale);
    }
    else if (value !== null && typeof value === 'object') {
        for (const locale of ctx.locales) {
            const text = value[locale];
            if (typeof text !== 'string' || text.trim() === '')
                missing.push(locale);
        }
    }
    if (missing.length === 0)
        return;
    const message = `Missing translation for ${missing.join(', ')}`;
    if (ctx.translationSeverity === 'error')
        err(ctx, 'W_MISSING_TRANSLATION', path, message);
    else
        warn(ctx, 'W_MISSING_TRANSLATION', path, message);
}
// ---------------------------------------------------------------------------
// Publish rules (docs/00 §4.3b, docs/04 §14)
// ---------------------------------------------------------------------------
// `flattenNames` and `isWidening` used to live here. They are now
// `flattenDefinition` / `isWideningType` in ../migrate/flatten.js, imported
// above — three places were independently deciding what "the same field"
// means across versions (here, the builder's panel, and the documented
// `diffDefinitions`), which is how a builder tells an author a change is safe
// while the validator refuses to publish it.
/** Widening to `text` is the one type change that keeps existing answers (docs/04 §14.2). */
function checkPublishRules(def, previous, opts, errors, warnings) {
    const push = (code, path, message, hint) => {
        errors.push(hint === undefined ? { code, path, message } : { code, path, message, hint });
    };
    if (def.id !== previous.id) {
        push('E_BREAKING_CHANGE', ptr('id'), `Form id changed from "${previous.id}" to "${def.id}"; that is a new form`);
    }
    const cmp = compareVersions(String(def.version ?? ''), String(previous.version ?? ''));
    if (cmp === 0) {
        push('E_VERSION_REUSED', ptr('version'), `Version "${def.version}" was already published; a published (id, version) pair is immutable`);
    }
    else if (cmp < 0) {
        push('E_VERSION_NOT_MONOTONIC', ptr('version'), `Version "${def.version}" is not greater than the published "${previous.version}"`);
    }
    else if (stableStringify(withoutVolatile(def)) === stableStringify(withoutVolatile(previous))) {
        push('E_PUBLISH_UNCHANGED', '', 'Nothing changed since the previous version except the version string');
    }
    const before = flattenDefinition(previous);
    const after = flattenDefinition(def);
    for (const [name, current] of after) {
        const old = before.get(name);
        if (old) {
            if (old.type !== current.type) {
                if (isWideningType(old.type, current.type)) {
                    warnings.push({
                        code: 'W_TYPE_WIDENED',
                        path: current.path,
                        message: `"${name}" widened from ${old.type} to text; existing answers are converted on migration`,
                    });
                }
                else {
                    push('E_BREAKING_CHANGE', current.path, `"${name}" changed type from ${old.type} to ${current.type}`, 'Use a new name, or publish with force and a migration plan');
                }
            }
            if (old.repeatPath !== current.repeatPath) {
                push('E_BREAKING_CHANGE', current.path, `"${name}" moved between repeat scopes ("${old.repeatPath || '<root>'}" → "${current.repeatPath || '<root>'}")`);
            }
            continue;
        }
        const retiredType = opts.retiredNames?.[name];
        if (retiredType !== undefined && retiredType !== current.type) {
            push('E_BREAKING_CHANGE', current.path, `"${name}" is a retired name published as ${retiredType}; it cannot be reused as ${current.type}`);
        }
    }
    for (const [name, old] of before) {
        if (!after.has(name)) {
            warnings.push({
                code: 'W_ELEMENT_REMOVED',
                path: ptr('pages'),
                message: `"${name}" (${old.type}) was removed; the name is now retired and drafts need a drop step`,
            });
        }
    }
}
function withoutVolatile(def) {
    const { meta, version: _version, ...rest } = def;
    const { updatedAt: _updatedAt, ...metaRest } = (meta ?? {});
    return { ...rest, meta: metaRest };
}
/**
 * Deterministic key-sorted JSON, used only to compare two definitions for
 * equality. This is *not* `canonicalJson()` (RFC 8785) and never feeds a hash.
 */
function stableStringify(value) {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(stableStringify).join(',')}]`;
    const record = value;
    const keys = Object.keys(record).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(record[k])}`).join(',')}}`;
}
// Re-exported so `@rasd/core/schema` keeps the export surface docs/17 §2.5
// froze, even though the implementation moved out of this (zod-bearing) module.
export { compareVersions };
//# sourceMappingURL=validate.js.map